import { useState, useEffect } from "react";
import { parseISO } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { 
  Users, 
  Search, 
  Calendar, 
  AlertTriangle, 
  CheckCircle2, 
  Clock,
  TrendingUp,
  MapPin,
  Eye,
  Download
} from "lucide-react";

const EquipoDashboardPage = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewType, setViewType] = useState("directos"); // 'directos' o 'todos'
  const [filterStatus, setFilterStatus] = useState(null); // null | 'critico' | 'advertencia'

  const fetchTeam = async () => {
    setLoading(true);
    try {
      const response = await api.getTeamStatistics(viewType);
      setTeam(response.data);
    } catch (error) {
      console.error("Error fetching team stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, [viewType]);

  const filteredTeam = team.filter((member) => {
    const matchesSearch = member.nombre_completo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.puesto.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterStatus === 'critico') return matchesSearch && member.dias_disponibles >= 25;
    if (filterStatus === 'advertencia') return matchesSearch && member.dias_disponibles >= 15 && member.dias_disponibles < 25;
    
    return matchesSearch;
  });

  const handleDownloadCSV = () => {
    if (filteredTeam.length === 0) {
      toast.error("No hay datos para descargar");
      return;
    }

    const headers = ["Nombre Completo", "Puesto", "Días Disponibles", "Días Usados", "Último Descanso", "Es Directo"];
    const csvContent = [
      headers.join(","),
      ...filteredTeam.map(m => [
        `"${m.nombre_completo}"`,
        `"${m.puesto}"`,
        m.dias_disponibles,
        m.total_usado,
        `"${formatDate(m.ultimo_descanso)}"`,
        m.es_directo ? "Sí" : "No"
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `equipo_vacaciones_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success("CSV descargado exitosamente");
  };

  const getStatusColor = (days) => {
    if (days >= 25) return "text-red-600 bg-red-50 border-red-200";
    if (days >= 15) return "text-amber-600 bg-amber-50 border-amber-200";
    return "text-emerald-600 bg-emerald-50 border-emerald-200";
  };

  const getProgressColor = (days) => {
    if (days >= 25) return "bg-red-500";
    if (days >= 15) return "bg-amber-500";
    return "bg-emerald-500";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return parseISO(dateString).toLocaleDateString("es-GT", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const criticalCount = team.filter(m => m.dias_disponibles >= 25).length;
  const warningCount = team.filter(m => m.dias_disponibles >= 15 && m.dias_disponibles < 25).length;

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Estatus de Equipo</h1>
        <p className="text-slate-500 mt-1">Monitorea el balance de vacaciones de tus colaboradores</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card 
          className={`border-slate-100 bg-white cursor-pointer transition-all hover:shadow-md ${!filterStatus ? 'ring-2 ring-sky-500' : ''}`}
          onClick={() => setFilterStatus(null)}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Colaboradores</p>
                <h3 className="text-3xl font-bold text-slate-800">{team.length}</h3>
              </div>
              <div className="p-3 bg-sky-50 text-sky-600 rounded-xl">
                <Users className="w-6 h-6" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-4 underline decoration-sky-500/30">Total equipo activo</p>
          </CardContent>
        </Card>

        <Card 
          className={`border-red-100 bg-red-50/30 cursor-pointer transition-all hover:shadow-md ${filterStatus === 'critico' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setFilterStatus('critico')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">Alerta Crítica</p>
                <h3 className="text-3xl font-bold text-red-700">{criticalCount}</h3>
              </div>
              <div className="p-3 bg-red-100 text-red-600 rounded-xl">
                <AlertTriangle className="w-6 h-6" />
              </div>
            </div>
            <p className="text-xs text-red-500 mt-4 font-medium">Colaboradores con +25 días</p>
          </CardContent>
        </Card>

        <Card 
          className={`border-amber-100 bg-amber-50/30 cursor-pointer transition-all hover:shadow-md ${filterStatus === 'advertencia' ? 'ring-2 ring-amber-500' : ''}`}
          onClick={() => setFilterStatus('advertencia')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600">En Advertencia</p>
                <h3 className="text-3xl font-bold text-amber-700">{warningCount}</h3>
              </div>
              <div className="p-3 bg-amber-100 text-amber-600 rounded-xl">
                <Clock className="w-6 h-6" />
              </div>
            </div>
            <p className="text-xs text-amber-500 mt-4 font-medium">Colaboradores con +15 días</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Table Section */}
      <Card className="border-slate-100 shadow-sm overflow-hidden">
        <CardHeader className="bg-white border-b border-slate-50">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-xl">Listado de Colaboradores</CardTitle>
              <CardDescription>Visualiza el uso individual de vacaciones</CardDescription>
            </div>
            <div className="flex flex-col md:flex-row md:items-center gap-2">
              <div className="flex bg-slate-100 p-1 rounded-lg">
                <Button 
                  variant={viewType === "directos" ? "white" : "ghost"} 
                  size="sm" 
                  className={`text-xs h-8 ${viewType === "directos" ? "shadow-sm" : "text-slate-500"}`}
                  onClick={() => setViewType("directos")}
                >
                  Directos
                </Button>
                <Button 
                  variant={viewType === "todos" ? "white" : "ghost"} 
                  size="sm" 
                  className={`text-xs h-8 ${viewType === "todos" ? "shadow-sm" : "text-slate-500"}`}
                  onClick={() => setViewType("todos")}
                >
                  Equipo Completo
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs border-slate-200"
                onClick={handleDownloadCSV}
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Descargar Lista
              </Button>
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre..."
                  className="pl-10 h-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50/50">
              <TableRow>
                <TableHead className="w-[250px]">Colaborador</TableHead>
                <TableHead>Días Disponibles</TableHead>
                <TableHead className="hidden md:table-cell">Últimas Vacaciones</TableHead>
                <TableHead>Días Usados</TableHead>
                <TableHead className="text-right">Acción</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTeam.length > 0 ? (
                filteredTeam.map((member) => (
                  <TableRow key={member.id} className="hover:bg-slate-50/50 transition-colors">
                    <TableCell>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800">{member.nombre_completo}</span>
                          {!member.es_directo && (
                            <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4 bg-slate-100 text-slate-500 border-none">
                              Indirecto
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-slate-500">{member.puesto}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2 max-w-[150px]">
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className={`font-bold ${getStatusColor(member.dias_disponibles)}`}>
                            {member.dias_disponibles} días
                          </Badge>
                        </div>
                        <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${getProgressColor(member.dias_disponibles)}`} 
                            style={{ width: `${Math.min((member.dias_disponibles / 30) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center gap-2 text-slate-600 text-sm">
                        <Calendar className="w-4 h-4" />
                        {formatDate(member.ultimo_descanso)}
                      </div>
                    </TableCell>
                    <TableCell>
                       <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm font-medium text-slate-700">{member.total_usado} días</span>
                       </div>
                    </TableCell>
                    <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                            onClick={() => window.location.href = `/empleados/${member.id}`}
                            title="Ver Detalle"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            <span className="text-xs">Detalle</span>
                          </Button>

                          {member.dias_disponibles >= 25 ? (
                            <Badge className="bg-red-100 text-red-600 border-red-200 hover:bg-red-200 cursor-default">
                              Alerta Crítica
                            </Badge>
                          ) : member.dias_disponibles >= 15 ? (
                            <Badge className="bg-amber-100 text-amber-600 border-amber-200 hover:bg-amber-200 cursor-default">
                              Advertencia
                            </Badge>
                          ) : (
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                          )}
                        </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="h-32 text-center text-slate-500">
                    No se encontraron colaboradores para la búsqueda.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Tips / Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
         <div className="p-4 bg-sky-50 border border-sky-100 rounded-xl flex gap-3">
            <CheckCircle2 className="w-6 h-6 text-sky-600 shrink-0" />
            <div>
              <h4 className="font-semibold text-sky-800 text-sm">Consejo de Bienestar</h4>
              <p className="text-xs text-sky-700 mt-1">
                Fomenta que los colaboradores con más de 15 días disponibles tomen al menos una semana de descanso para prevenir el agotamiento y mejorar la productividad.
              </p>
            </div>
         </div>
         <div className="p-4 bg-orange-50 border border-orange-100 rounded-xl flex gap-3">
            <AlertTriangle className="w-6 h-6 text-orange-600 shrink-0" />
            <div>
              <h4 className="font-semibold text-orange-800 text-sm">Ley de Vacaciones</h4>
              <p className="text-xs text-orange-700 mt-1">
                Recuerda que las vacaciones son irrenunciables y se deben gozar anualmente para cumplir con la normativa laboral vigente y el bienestar del personal.
              </p>
            </div>
         </div>
      </div>
    </div>
  );
};

export default EquipoDashboardPage;
